<script setup lang="ts">
import { computed } from 'vue'
import type { RouteLocationRaw } from 'vue-router'
import { RouterLink } from 'vue-router'
import { FairIcon } from '@/ui/primitives/FairIcon'
import type {
  FairCapsuleDevice,
  FairCapsuleItem,
  FairCapsuleSize,
  FairCapsuleState,
  FairCapsuleType,
} from '@/ui/tokens/fair-capsule.data'

interface Props {
  items: FairCapsuleItem[]
  type?: FairCapsuleType
  size?: FairCapsuleSize
  device?: FairCapsuleDevice
  visible?: boolean
  showAiTips?: boolean
  showText?: boolean
  name?: string
  description?: string
  currentLook?: number
  totalLooks?: number
  /** Full counter label, e.g. "Look 1/20". When set, overrides default English format. */
  lookLabel?: string
  favorite?: boolean
  interactive?: boolean
  previewState?: FairCapsuleState
  /** Crawlable destination — overlay <a> */
  to?: RouteLocationRaw
}

const props = withDefaults(defineProps<Props>(), {
  type: 'preview',
  size: 'L',
  device: 'desktop',
  visible: true,
  showAiTips: true,
  showText: false,
  name: 'Casual Comfort Capsule',
  description: 'Relaxed essentials for everyday wear, blending style and ease.',
  currentLook: 1,
  totalLooks: 20,
  lookLabel: undefined,
  favorite: false,
  interactive: true,
  previewState: undefined,
  to: undefined,
})

const emit = defineEmits<{
  click: [event: MouseEvent]
  dislike: [event: MouseEvent]
  favorite: [event: MouseEvent]
  'prev-look': [event: MouseEvent]
  'next-look': [event: MouseEvent]
}>()

const visualState = computed(() => props.previewState ?? 'idle')

const isCard = computed(() => props.type === 'card')
const isPreview = computed(() => props.type === 'preview')

const showTextBlock = computed(() => {
  if (!isPreview.value) return false
  if (props.device === 'desktop') return true
  return props.showText
})

const showHoverOverlay = computed(() => {
  return isPreview.value && props.device === 'desktop' && props.visible
})

const lookCounter = computed(() => {
  if (props.lookLabel) return props.lookLabel
  return `Look ${props.currentLook}/${props.totalLooks}`
})

const rootClass = computed(() => ({
  'fair-capsule': true,
  [`fair-capsule--${props.device}`]: true,
  [`fair-capsule--${props.size}`]: true,
  [`fair-capsule--${props.type}`]: true,
  [`fair-capsule--${visualState.value}`]: true,
  'fair-capsule--invisible': !props.visible,
  'fair-capsule--non-interactive': !props.interactive,
}))

function getItemByType(type: string): FairCapsuleItem | undefined {
  return props.items.find((item) => item.type === type)
}

function handleRootClick(event: MouseEvent) {
  if (!props.interactive) return
  emit('click', event)
}

function stopAndEmit(
  event: MouseEvent,
  name: 'dislike' | 'favorite' | 'prev-look' | 'next-look',
) {
  event.stopPropagation()
  if (!props.interactive) return
  if (name === 'dislike') emit('dislike', event)
  else if (name === 'favorite') emit('favorite', event)
  else if (name === 'prev-look') emit('prev-look', event)
  else if (name === 'next-look') emit('next-look', event)
}

const favoriteIcon = computed(() =>
  props.favorite ? 'favorite-filled' : 'favorite-outlined',
)
</script>

<template>
  <article
    :class="rootClass"
    @click="handleRootClick"
  >
    <RouterLink
      v-if="to"
      :to="to"
      class="fair-capsule__seo-link"
      :aria-label="name"
      @click.stop
    />

    <!-- Card header: dislike / counter / favorite -->
    <div
      v-if="isCard"
      class="fair-capsule__header"
    >
      <button
        type="button"
        class="fair-capsule__header-btn"
        aria-label="Dislike"
        @click="stopAndEmit($event, 'dislike')"
      >
        <FairIcon
          name="dislike"
          :size="20"
          color="var(--fair-color-primary-blue-100)"
        />
      </button>
      <p class="fair-capsule__header-counter">
        {{ lookCounter }}
      </p>
      <button
        type="button"
        class="fair-capsule__header-btn"
        :aria-pressed="favorite"
        aria-label="Favorite"
        @click="stopAndEmit($event, 'favorite')"
      >
        <FairIcon
          :name="favoriteIcon"
          :size="20"
          color="var(--fair-color-primary-blue-100)"
        />
      </button>
    </div>

    <!-- Clothes block with gradient background -->
    <div class="fair-capsule__clothes">
      <!-- Outerwear — left -->
      <div
        v-if="getItemByType('outerwear')"
        class="fair-capsule__item fair-capsule__item--outerwear"
      >
        <div class="fair-capsule__item-media">
          <img
            class="fair-capsule__item-img fair-capsule__item-img--cover"
            :src="getItemByType('outerwear')!.imageUrl"
            :alt="getItemByType('outerwear')!.alt || 'Outerwear'"
          >
        </div>
        <div
          v-if="showAiTips"
          class="fair-capsule__ai-tip"
          aria-hidden="true"
        />
      </div>

      <!-- Bottom / Dress — right -->
      <div
        v-if="getItemByType('bottom')"
        class="fair-capsule__item fair-capsule__item--bottom"
      >
        <div class="fair-capsule__item-media">
          <img
            class="fair-capsule__item-img fair-capsule__item-img--cover"
            :src="getItemByType('bottom')!.imageUrl"
            :alt="getItemByType('bottom')!.alt || 'Bottom'"
          >
        </div>
        <div
          v-if="showAiTips"
          class="fair-capsule__ai-tip"
          aria-hidden="true"
        />
      </div>

      <!-- Top — center -->
      <div
        v-if="getItemByType('top')"
        class="fair-capsule__item fair-capsule__item--top"
      >
        <div class="fair-capsule__item-media">
          <img
            class="fair-capsule__item-img"
            :src="getItemByType('top')!.imageUrl"
            :alt="getItemByType('top')!.alt || 'Top'"
          >
        </div>
        <div
          v-if="showAiTips"
          class="fair-capsule__ai-tip"
          aria-hidden="true"
        />
      </div>

      <!-- Accessory 1 — top-left area -->
      <div
        v-if="getItemByType('accessory-1')"
        class="fair-capsule__item fair-capsule__item--accessory-1"
      >
        <div class="fair-capsule__item-media">
          <img
            class="fair-capsule__item-img fair-capsule__item-img--cover"
            :src="getItemByType('accessory-1')!.imageUrl"
            :alt="getItemByType('accessory-1')!.alt || 'Accessory'"
          >
        </div>
        <div
          v-if="showAiTips"
          class="fair-capsule__ai-tip"
          aria-hidden="true"
        />
      </div>

      <!-- Accessory 2 — top-right area -->
      <div
        v-if="getItemByType('accessory-2')"
        class="fair-capsule__item fair-capsule__item--accessory-2"
      >
        <div class="fair-capsule__item-media">
          <img
            class="fair-capsule__item-img fair-capsule__item-img--cover"
            :src="getItemByType('accessory-2')!.imageUrl"
            :alt="getItemByType('accessory-2')!.alt || 'Accessory'"
          >
        </div>
        <div
          v-if="showAiTips"
          class="fair-capsule__ai-tip"
          aria-hidden="true"
        />
      </div>

      <!-- Bag — bottom-left -->
      <div
        v-if="getItemByType('bag')"
        class="fair-capsule__item fair-capsule__item--bag"
      >
        <div class="fair-capsule__item-media">
          <img
            class="fair-capsule__item-img"
            :src="getItemByType('bag')!.imageUrl"
            :alt="getItemByType('bag')!.alt || 'Bag'"
          >
        </div>
        <div
          v-if="showAiTips"
          class="fair-capsule__ai-tip"
          aria-hidden="true"
        />
      </div>

      <!-- Shoes — bottom-right -->
      <div
        v-if="getItemByType('shoes')"
        class="fair-capsule__item fair-capsule__item--shoes"
      >
        <div class="fair-capsule__item-media">
          <img
            class="fair-capsule__item-img"
            :src="getItemByType('shoes')!.imageUrl"
            :alt="getItemByType('shoes')!.alt || 'Shoes'"
          >
        </div>
        <div
          v-if="showAiTips"
          class="fair-capsule__ai-tip"
          aria-hidden="true"
        />
      </div>

      <!-- Hover overlay (desktop preview only) -->
      <div
        v-if="showHoverOverlay"
        class="fair-capsule__hover-overlay"
      >
        <p class="fair-capsule__hover-title">
          {{ lookCounter }}
        </p>
        <div class="fair-capsule__hover-nav">
          <button
            type="button"
            class="fair-capsule__hover-nav-btn"
            aria-label="Previous look"
            @click="stopAndEmit($event, 'prev-look')"
          >
            <FairIcon
              name="arrow-left"
              :size="20"
              color="var(--fair-color-primary-blue-100)"
            />
          </button>
          <button
            type="button"
            class="fair-capsule__hover-nav-btn"
            aria-label="Next look"
            @click="stopAndEmit($event, 'next-look')"
          >
            <FairIcon
              name="arrow-right"
              :size="20"
              color="var(--fair-color-primary-blue-100)"
            />
          </button>
        </div>
      </div>
    </div>

    <!-- Text block (Preview type only) -->
    <div
      v-if="showTextBlock && visible"
      class="fair-capsule__text"
    >
      <p class="fair-capsule__title">
        {{ name }}
      </p>
      <p class="fair-capsule__caption">
        {{ description }}
      </p>
    </div>
  </article>
</template>

<style>
@import '@/ui/tokens/fair-capsule.css';
</style>
